<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTeamInvitations } from '@/composables/useTeamInvitations'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import InvitationsTable from './InvitationsTable.vue'
import SendInvitationForm from './SendInvitationForm.vue'
import type { Invitation } from '@/types/team'

const props = defineProps<{
  organisationId: number
  isOwner: boolean
}>()

const { t } = useI18n()
const {
  invitations,
  loading,
  saving,
  generalError,
  actionErrorMessage,
  fetchInvitations,
  cancelInvitation,
  resendInvitation,
} = useTeamInvitations()

const successMessage = ref<string | null>(null)
const confirmCancelId = ref<number | null>(null)

function requestCancel(invitationId: number): void {
  if (saving.value) return
  confirmCancelId.value = invitationId
}

function dismissCancel(open: boolean): void {
  if (!open && !saving.value) {
    confirmCancelId.value = null
  }
}

async function confirmCancel(): Promise<void> {
  if (confirmCancelId.value === null || saving.value) return
  const invitationId = confirmCancelId.value
  const email = invitations.value.find((inv) => inv.id === invitationId)?.email ?? ''
  successMessage.value = null
  const success = await cancelInvitation(props.organisationId, invitationId)
  confirmCancelId.value = null
  if (success) {
    successMessage.value = t('team.invitations.cancel_success', { email })
  }
}

async function handleResend(invitationId: number): Promise<void> {
  if (saving.value) return
  successMessage.value = null
  const updated = await resendInvitation(props.organisationId, invitationId)
  if (updated) {
    successMessage.value = t('team.invitations.resend_success', { email: updated.email })
  }
}

function handleSent(_invitation: Invitation): void {
  // Invitation already added to state by the composable
}

onMounted(() => {
  fetchInvitations(props.organisationId)
})
</script>

<template>
  <div>
    <!-- Action feedback banners -->
    <div
      v-if="successMessage"
      class="mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
      role="status"
    >
      {{ successMessage }}
    </div>
    <div
      v-if="actionErrorMessage"
      class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      role="alert"
    >
      {{ actionErrorMessage }}
    </div>

    <!-- General error -->
    <div
      v-if="generalError"
      class="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
      role="alert"
    >
      {{ t(generalError) }}
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="space-y-3 mb-4">
      <div
        v-for="i in 2"
        :key="i"
        class="h-10 rounded-lg bg-slate-700/50 animate-pulse"
      />
    </div>

    <!-- Invitations table -->
    <InvitationsTable
      v-else
      :organisation-id="organisationId"
      :invitations="invitations"
      :is-owner="isOwner"
      :busy="saving"
      @cancel="requestCancel"
      @resend="handleResend"
    />

    <!-- Send invitation form (owner only) -->
    <div v-if="isOwner" class="mt-6">
      <SendInvitationForm
        :organisation-id="organisationId"
        @sent="handleSent"
      />
    </div>

    <ConfirmDialog
      :open="confirmCancelId !== null"
      :title="t('team.invitations.cancel_confirm_title')"
      :body="t('team.invitations.cancel_confirm_body')"
      :confirm-label="t('team.invitations.cancel_confirm_label')"
      :loading="saving"
      danger
      @update:open="dismissCancel"
      @confirm="confirmCancel"
    />
  </div>
</template>
